import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Snackbar } from "./snackbar.interface";
import { SnackbarService } from "./snackbar.service";

@Component({
  selector: "flx-snackbar",
  template: `
    <div class="pl-snackbars" [ngClass]="position || 'bottom-right'">
      <flx-snack
        *ngFor="let snackbar of snacks"
        [background]="snackbar.background || background"
        [customClass]="snackbar.customClass || customClass"
        [color]="
          snackbar.color ||
          color ||
          calcTextColor(snackbar.background || background)
        "
      >
        <div class="pl-snack--content">
          <div
            class="pl-snack--text"
            [innerHtml]="snackbar.msg | safeHtml"
          ></div>

          <div
            *ngIf="snackbar.action?.text"
            class="pl-snack--action"
            (click)="onClick(snackbar)"
            [ngStyle]="{ color: snackbar.action.color || accent }"
          >
            {{ snackbar.action.text }}
          </div>
        </div>

        <div class="pl-snack--close" *ngIf="!snackbar.hideClose">
          <button
            type="button"
            class="close"
            aria-label="Close"
            (click)="remove(snackbar.id)"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </flx-snack>
    </div>
  `,
  styleUrls: ["./snackbar.component.scss"]
})
export class SnackbarComponent {
  @Input() position:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
  @Input() max: number;
  @Input() background: string;
  @Input() accent: string;
  @Input() color: string;
  @Input() customClass: any;
  @Input() timeout: number;

  @Output() onAdd: EventEmitter<any> = new EventEmitter<any>();
  @Output() onRemove: EventEmitter<any> = new EventEmitter<any>();
  @Output() onClear: EventEmitter<boolean> = new EventEmitter<boolean>();

  snacks: Array<Snackbar> = [];

  constructor(private snackbarService: SnackbarService) {
    this.snackbarService.get().subscribe(snack => {
      if (snack.action === "add") {
        this.add(snack.data);
      } else if (snack.action === "remove") {
        this.remove(snack.id);
      } else if (snack.action === "clear") {
        this.clear();
      }
    });
  }

  add(snack) {
    let timeout;
    const id = this.uuid();

    if (this.max && this.max > 0 && this.snacks.length === this.max) {
      this.remove(this.snacks[0].id);
    }

    if (snack.timeout || this.timeout) {
      timeout = setTimeout(() => {
        this.remove(id);
      }, snack.timeout || this.timeout);
    }

    const data: Snackbar = { id, timeoutObj: timeout, ...snack };

    if (snack.onAdd) {
      snack.onAdd(data);
    }

    if (this.onAdd) {
      this.onAdd.emit(data);
    }

    this.snacks.push(data);
  }

  onClick(snack) {
    const fcn = snack.action.onClick;
    // tslint:disable-next-line: no-unused-expression
    fcn && typeof fcn === "function" && fcn(snack);

    this.remove(snack.id);
  }

  remove(id) {
    const snack = this.snacks.find(obj => obj.id === id);

    if (snack) {
      if (snack.onRemove) {
        snack.onRemove(snack);
      }

      if (this.onRemove) {
        this.onRemove.emit(snack);
      }

      if (snack.timeoutObj) {
        clearTimeout(snack.timeoutObj);
      }
    }

    this.snacks = this.snacks.filter(obj => obj.id !== id);
  }

  clear() {
    this.snacks = [];

    if (this.onClear) {
      this.onClear.emit(true);
    }
  }

  uuid() {
    // tslint:disable:no-bitwise
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    // tslint:enable:no-bitwise
  }

  calcTextColor(background) {
    if (!background) {
      return null;
    }

    function hexToRgb(hex) {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
      });

      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
          }
        : null;
    }

    const rgb = hexToRgb(background);
    if (!rgb) {
      return null;
    }

    const color = [rgb.r / 255, rgb.g / 255, rgb.b / 255];

    for (let i = 0; i < color.length; ++i) {
      if (color[i] <= 0.03928) {
        color[i] = color[i] / 12.92;
      } else {
        color[i] = Math.pow((color[i] + 0.055) / 1.055, 2.4);
      }
    }

    const l = 0.2126 * color[0] + 0.7152 * color[1] + 0.0722 * color[2];

    if (l > 0.179) {
      return "#000";
    } else {
      return "#fff";
    }
  }
}
